/**
 * Sidebar Button Component
 * 
 * A reusable button component that matches the design style of sidebar buttons
 * (like heart, follow, share buttons). Features rounded-full shape, glass effect,
 * and smooth hover/active animations.
 * 
 * Usage:
 * ```tsx
 * <SidebarButton onClick={handleClick}>
 *   <Plus className="h-5 w-5" />
 *   <span>New Item</span>
 * </SidebarButton>
 * ```
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ButtonProps } from '@/components/ui/button';

export interface SidebarButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
  /**
   * Optional icon element to display before the children
   */
  icon?: React.ReactNode;
  /**
   * Whether to show the button in a compact size (smaller padding)
   */
  compact?: boolean;
}

/**
 * SidebarButton - A button component matching the sidebar button design
 * 
 * Features:
 * - Rounded-full shape
 * - Glass effect (nav-glass)
 * - Smooth hover/active animations
 * - Responsive sizing
 * - Touch-optimized
 */
export function SidebarButton({
  children,
  icon,
  compact = false,
  className,
  ...props
}: SidebarButtonProps) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        // Base sizing - responsive
        'h-12 sm:h-10',
        compact ? 'px-3 sm:px-2.5' : 'px-4 sm:px-3.5',
        'rounded-full',
        // Background
        'bg-white dark:bg-gray-900',
        // Shadows - subtle to match other buttons
        'shadow-[0_1px_3px_rgba(0,0,0,0.06)]',
        'hover:shadow-[0_2px_6px_rgba(0,0,0,0.1)]',
        // Hover/active effects
        'hover:scale-105',
        'active:scale-95',
        'transition-all duration-150 ease-out',
        'hover:bg-white dark:hover:bg-gray-800',
        // Touch optimization
        'touch-manipulation',
        'min-h-[48px] sm:min-h-[44px]',
        // Layout
        'flex items-center gap-2.5',
        className
      )}
      {...props}
    >
      {icon && (
        <span className="transition-transform duration-150">
          {icon}
        </span>
      )}
      {children && (
        <span className="text-base sm:text-sm font-medium text-foreground">
          {children}
        </span>
      )}
    </Button>
  );
}

